import { useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { ROLE_OPTIONS } from '../selfSort.constants';

const OTHER = 'Other';
// 'Other' is a fallback, not a normal choice — it's appended only when a search
// matches nothing, so it never crowds out the real roles in the default list.
const SELECTABLE_ROLES = ROLE_OPTIONS.filter((role) => role !== OTHER);

type Props = {
  value: string | null;
  onChange: (value: string) => void;
  /** Open (and focus) the dropdown as soon as the control mounts — used when the
   * owner accordion auto-advances to the role question. */
  autoOpen?: boolean;
  /** Called when the dropdown is dismissed by an outside click (not by picking a
   * value), so the parent can collapse the whole question (#2621). */
  onRequestCollapse?: () => void;
};

/** Searchable role picker — used by both the owner and member variants. */
export default function RoleSelect({ value, onChange, autoOpen, onRequestCollapse }: Props) {
  const [search, setSearch] = useState('');
  // Picking an option both fires onChange and closes the popup; that close must
  // NOT collapse the question (the parent already advances on answer). This flag
  // distinguishes a value-selection close from a true outside click.
  const justSelectedRef = useRef(false);

  const options = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = query ? SELECTABLE_ROLES.filter((role) => role.toLowerCase().includes(query)) : SELECTABLE_ROLES;
    const roles = matches.length > 0 ? matches : [OTHER];
    return roles.map((role) => ({ label: role, value: role }));
  }, [search]);

  const handleChange = (next: string) => {
    justSelectedRef.current = true;
    onChange(next);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open) return;
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    onRequestCollapse?.();
  };

  return (
    <Select
      showSearch
      autoFocus={autoOpen}
      defaultOpen={autoOpen}
      value={value ?? undefined}
      onChange={handleChange}
      onSearch={setSearch}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder="Select your role"
      className="w-full"
      options={options}
      filterOption={false}
    />
  );
}
